import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @param {string} template
 * @param {string} amount
 * @returns {string}
 */
function savingsLabel(template, amount) {
  return (template || 'Save __amount__').split('__amount__').join(amount);
}

class GioCartLineSavings extends HTMLElement {
  /** @type {AbortController | null} */
  #fetchController = null;

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.#refresh();
  }

  disconnectedCallback() {
    this.#fetchController?.abort();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #handleCartUpdate = (event) => {
    if (!event.promise) {
      this.#refresh();
      return;
    }

    Promise.resolve(event.promise)
      .catch(() => null)
      .finally(() => this.#refresh());
  };

  async #refresh() {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    try {
      const url = new URL(this.dataset.cartUrl || '/cart.js', window.location.origin);
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: this.#fetchController.signal,
      });

      if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);
      this.#render(await response.json());
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.warn('[gio-cart-line-savings] Cart state could not be refreshed.', error);
    }
  }

  /** @param {{ currency?: string, items?: Array<Record<string, unknown>> }} cart */
  #render(cart) {
    const currency = String(cart.currency || this.dataset.currency || 'USD');
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const itemsByKey = new Map((cart.items || []).map((item) => [String(item.key || ''), item]));

    for (const priceCell of this.querySelectorAll('[data-gio-cart-line-price]')) {
      if (!(priceCell instanceof HTMLElement)) continue;
      const item = itemsByKey.get(priceCell.dataset.lineKey || '');
      if (!item) continue;

      const originalLinePrice = Number(item.original_line_price || 0);
      const discountFromAllocations = Array.isArray(item.discounts)
        ? item.discounts.reduce((total, discount) => total + Number(discount?.amount || 0), 0)
        : 0;
      const lineDiscount = Math.max(0, Number(item.total_discount || 0), discountFromAllocations);
      const discountedLinePrice = Math.max(0, originalLinePrice - lineDiscount);
      const regular = priceCell.querySelector('[data-gio-cart-line-regular]');
      const discounted = priceCell.querySelector('[data-gio-cart-line-discounted]');

      if (regular instanceof HTMLElement) regular.hidden = lineDiscount > 0;
      if (!(discounted instanceof HTMLElement)) continue;
      discounted.hidden = lineDiscount <= 0;
      if (lineDiscount <= 0) continue;

      const current = discounted.querySelector('[data-gio-cart-line-current]');
      const original = discounted.querySelector('[data-gio-cart-line-original]');
      const saving = discounted.querySelector('[data-gio-cart-line-saving]');
      const formattedCurrent = formatMoney(discountedLinePrice, moneyFormat, currency);
      const formattedOriginal = formatMoney(originalLinePrice, moneyFormat, currency);
      const formattedSaving = formatMoney(lineDiscount, moneyFormat, currency);

      if (current instanceof HTMLElement) {
        current.textContent = formattedCurrent;
        current.setAttribute('value', formattedCurrent);
      }
      if (original) original.textContent = formattedOriginal;
      if (saving) saving.textContent = savingsLabel(this.dataset.saveTemplate || '', formattedSaving);
    }
  }
}

if (!customElements.get('gio-cart-line-savings')) {
  customElements.define('gio-cart-line-savings', GioCartLineSavings);
}
